import { useState, useMemo } from "react";
import { ArrowLeft, Search, User, Stethoscope, DollarSign, CheckCircle } from "lucide-react";
import PropTypes from "prop-types";
import useDebounce from "../hooks/useDebounce";
import usePartners from "../../posConsult/hooks/usePartners";
import formatMoney from '../../../utils/formatMoney';
import "./../css/doctorSelectionPage.css";

const DoctorSelectionPage = ({ patient, procedures, onSelectDoctor, onBack }) => {
  const [searchTerm, setSearchTerm] = useState("");

  const procedureIds = useMemo(() => procedures.map((p) => p.procedure_id || p.id), [procedures]);
  const debouncedProcedureIds = useDebounce(procedureIds, 500);
  
  const { partners, loading: loadingDoctors } = usePartners(debouncedProcedureIds);
  const suggestions = partners?.data?.suggestions ?? [];
  const allInsurance = procedures.every((p) => p.payment?.type === "insurance");

  const doctorsWithPrices = useMemo(() => {
    return suggestions
      .filter((doctor) => {
        const doctorProcedureIds = doctor.procedures?.map((p) => String(p.id)) || [];
        return procedures
          .map((p) => String(p.procedure_id || p.id))
          .every((code) => doctorProcedureIds.includes(code));
      })
      .map((doctor) => {
        let totalPrice = 0;
        const prices = {};
        const proceduresMap = [];

        doctor.procedures.forEach((proc) => {
          const selling = Number(proc.selling_price ?? 0);
          const cost = Number(proc.cost_price ?? 0);

          prices[proc.id] = selling;
          if (proc.payment?.type !== "insurance") {
            totalPrice += selling;
          }

          proceduresMap.push({
            id: proc.id,
            name: proc.name,
            code: proc.code,
            payment: proc.payment,
            selling_price: selling,
            cost_price: cost,
          });
        });

        return { ...doctor, prices, totalPrice, procedures: proceduresMap };
      })
      .sort((a, b) => a.totalPrice - b.totalPrice);
  }, [suggestions, procedures]);

  const filteredDoctors = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return doctorsWithPrices.filter(
      (doctor) =>
        doctor.name?.toLowerCase().includes(term) ||
        doctor.specialty?.toLowerCase().includes(term)
    );
  }, [searchTerm, doctorsWithPrices]);

  return (
    <div className="page-container">
      <div className="page-header">
        <button className="back-button" onClick={onBack}>
          <ArrowLeft size={20} />
          <span>Voltar</span>
        </button>
        <h1>Selecionar Profissional</h1>
      </div>

      <div className="patient-summary">
        <div className="patient-info-header">
          <User size={20} />
          <h3>Paciente: {patient.name}</h3>
        </div>
        <div className="selected-procedures">
          <h4>Procedimentos selecionados:</h4>
          <ul>
            {procedures.map((proc) => (
              <li key={proc.id}>
                <span className="procedure-name">{proc.name}</span>
                <span className="procedure-code"> ({proc.cbhpm_code})</span>
                <span className="procedure-payment">
                   {" - "} {proc.payment?.type === "insurance" ? "Convênio" : "Particular"}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="search-container">
        <Search className="search-icon" />
        <input
          type="text"
          placeholder="Pesquisar médico por nome ou especialidade"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="doctors-list">
        {filteredDoctors.length > 0 ? (
          filteredDoctors.map((doctor) => (
            <div key={doctor.id} className="doctor-card" onClick={() => onSelectDoctor(doctor)}>
              <div className="doctor-avatar"><Stethoscope size={24} /></div>
              <div className="doctor-info">
                <h3>{doctor.name}</h3>
                <p className="doctor-specialty">{doctor.specialty || "Especialidade"}</p>
                <div className="doctor-procedures">
                  <p>Procedimentos:</p>
                  <ul>
                    {doctor.procedures.map((proc) => (
                      <li key={proc.id}>
                        <CheckCircle size={14} className="check-icon" />
                        <span>
                          {proc.name}: {proc.payment?.type === "insurance" ? "Convênio" : formatMoney(proc.selling_price)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="doctor-price">
                <DollarSign size={20} />
                <div className="price-info">
                  <span className="price-label">Valor&nbsp;Total:</span><br/>
                  <span className="price-value">{allInsurance ? "Convênio" : formatMoney(doctor.totalPrice)}</span>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="no-doctors">
            <p>{loadingDoctors ? "Carregando médicos..." : "Nenhum médico disponível para realizar estes procedimentos."}</p>
          </div>
        )}
      </div>
    </div>
  );
};

DoctorSelectionPage.propTypes = {
  patient: PropTypes.object.isRequired,
  procedures: PropTypes.array.isRequired,
  onSelectDoctor: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
};

export default DoctorSelectionPage;